import { UPSELL_ITEM_MAX_QUANTITY_CATERING, UPSELL_ITEM_MAX_QUANTITY } from '@nandosaus/constants';

import { noop } from 'lodash';
import { observer } from 'mobx-react';
import { productPropTypes } from '@nandosaus/prop-types';
import PropTypes from 'prop-types';
import { useRootStore } from '@nandosaus/state-management';
import { getNextCateringQuantity, getPreviousCateringQuantity } from '@utils/catering-quantity';
import { CartIndicator } from '@components/card/cart-indicator';
import styled from 'styled-components';

import { QuantityPicker } from '@components/quantity-picker';
import { Box, Flex } from '../grid';
import { P } from '../typography';
import { P as NewP } from '../typography/new';
import { Tags } from '../tags';
import { TextClamp } from '../text-clamp';
import { Card } from './card';
import { TextPill } from '../text-pill';
import { Button } from '../button';
import { Icon } from '../icons';
import { Image } from '../image';

const StyledCartIndicator = styled(CartIndicator)`
  visibility: ${({ visibility }) => visibility};
`;

// Upsell tile, configurable-items design. A white circular FAB sits in the bottom-right of
// the image: `+` for an item that can be quick-added, a pencil for one that has to be
// configured first. Once the item is in the cart the tile gains a dark border, a black
// quantity badge, and the FAB becomes a -/+ stepper.
const Fab = styled(Flex)`
  background: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};

  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
  `}

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      opacity: 0.8;
    }
  }
`;

const TileFab = ({ iconName, onClick, disabled, ariaLabel }) => (
  <Fab
    as="button"
    type="button"
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={event => {
      event.stopPropagation();
      onClick();
    }}
    height="40px"
    width="40px"
    minWidth="40px"
    alignItems="center"
    justifyContent="center"
  >
    <Icon name={iconName} fill="black" />
  </Fab>
);

TileFab.propTypes = {
  iconName: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string.isRequired,
};

TileFab.defaultProps = {
  disabled: false,
};

const ConfigurableUpsellItemTile = ({
  item,
  isNew,
  quantityInCart,
  requiresConfiguration,
  maxQuantity,
  onAdd,
  onCustomise,
  onIncrement,
  onDecrement,
}) => {
  const isInCart = quantityInCart > 0;
  const itemPrice = item?.prices?.formattedPrice;
  const itemKilojoules = item?.formattedKilojoules;
  const isUnavailable = !item.isAvailable;

  // `requiresConfiguration` is null when the product's details have not loaded, which means
  // `choices` is empty and cannot be trusted. Showing the customise control is the safe
  // answer: it routes through the PDP, which loads the details before asking anything.
  const needsConfiguration = requiresConfiguration !== false;

  return (
    <Flex flexDirection="column" width="100%" data-item-id={item.id}>
      <Box
        position="relative"
        borderRadius="8px"
        overflow="hidden"
        border={isInCart ? '3px solid' : '3px solid transparent'}
        borderColor={isInCart ? 'black' : 'transparent'}
      >
        <Box position="relative" width="100%" aspectRatio="1 / 1" backgroundColor="warmWhite3">
          {item.image && <Image src={item.image} alt={item.imageAlt} isBackground />}
        </Box>

        {isNew && (
          <TextPill
            backgroundColor="neonPink"
            width="50px"
            color="white"
            position="absolute"
            top="8px"
            left="8px"
            zIndex={1}
            text="NEW"
          />
        )}

        {isInCart && (
          <CartIndicator
            position="absolute"
            top="8px"
            right="8px"
            zIndex={2}
            bg="black"
            height={32}
            width={32}
            minHeight={32}
            minWidth={32}
            quantityInCart={quantityInCart}
          />
        )}

        <Flex position="absolute" bottom="8px" right="8px" zIndex={2} gap="8px" alignItems="center">
          {isInCart ? (
            <>
              <TileFab
                iconName="minus"
                ariaLabel={`Remove one ${item.name}`}
                onClick={onDecrement}
                disabled={isUnavailable}
              />
              <TileFab
                iconName="plus"
                ariaLabel={`Add another ${item.name}`}
                onClick={onIncrement}
                disabled={isUnavailable || quantityInCart >= maxQuantity}
              />
            </>
          ) : (
            <TileFab
              iconName={needsConfiguration ? 'pencil' : 'plus'}
              ariaLabel={needsConfiguration ? `Customise ${item.name}` : `Add ${item.name}`}
              onClick={needsConfiguration ? onCustomise : onAdd}
              disabled={isUnavailable}
            />
          )}
        </Flex>
      </Box>

      <Box mt="12px">
        <NewP variant="standard-semi-bold" color={isUnavailable ? 'grey70' : 'black'} mb="4px">
          <TextClamp>{item.groupName || item.name}</TextClamp>
        </NewP>
        <Flex gap="8px">
          {itemPrice && <NewP variant="standard">{itemPrice}</NewP>}
          {itemKilojoules && (
            <NewP variant="standard" color="grey70">
              {itemKilojoules}
            </NewP>
          )}
        </Flex>
        {isUnavailable && (
          <NewP variant="small" color="grey70">
            Sold Out
          </NewP>
        )}
      </Box>
    </Flex>
  );
};

ConfigurableUpsellItemTile.propTypes = {
  item: productPropTypes.isRequired,
  isNew: PropTypes.bool,
  quantityInCart: PropTypes.number,
  requiresConfiguration: PropTypes.bool,
  maxQuantity: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onCustomise: PropTypes.func.isRequired,
  onIncrement: PropTypes.func.isRequired,
  onDecrement: PropTypes.func.isRequired,
};

ConfigurableUpsellItemTile.defaultProps = {
  isNew: false,
  quantityInCart: 0,
  requiresConfiguration: null,
};

const UpsellItemCard = ({
  item,
  isNew,
  addItem,
  removeItem,
  addSelectedItemsToCart,
  isItemInCart,
  isDiscountApplied,
  quantitySelected,
  isConfigurableFlowEnabled,
  requiresConfiguration,
  onAdd,
  onCustomise,
  onIncrement,
  onDecrement,
}) => {
  const { MenuStore, CartStore } = useRootStore();
  const { isCateringMenu } = MenuStore;

  // Get catering-specific quantity settings from the item
  const cateringMinQty = item?.minQty || 1;
  const cateringQtyIncrement = item?.qtyIncrement || 1;

  const itemPrice = item?.prices?.formattedPrice;
  const itemKilojoules = item?.formattedKilojoules;
  const isEligibleForDeliveryDiscount = item?.isEligibleForDeliveryDiscount;

  const getQuantityInCart = () => {
    const quantityInCart = parseInt(CartStore.productQuantityInCart(item.id), 10);
    return quantityInCart;
  };

  const quantityInCart = getQuantityInCart();

  // Everything below this branch is the pre-flag tile, left exactly as it was.
  if (isConfigurableFlowEnabled) {
    return (
      <ConfigurableUpsellItemTile
        item={item}
        isNew={isNew}
        quantityInCart={quantityInCart}
        requiresConfiguration={requiresConfiguration}
        maxQuantity={isCateringMenu ? UPSELL_ITEM_MAX_QUANTITY_CATERING : UPSELL_ITEM_MAX_QUANTITY}
        onAdd={onAdd}
        onCustomise={onCustomise}
        onIncrement={onIncrement}
        onDecrement={onDecrement}
      />
    );
  }

  return (
    <Flex
      position="relative"
      border="1px solid transparent"
      height="100%"
      minWidth="150px"
      width="33%"
      data-item-id={item.id}
    >
      {isNew && (
        <TextPill
          backgroundColor="neonPink"
          width="50px"
          color="white"
          position="absolute"
          top={{ _: 1, lg: '0.5rem' }}
          left={{ _: 1, lg: '0.5rem' }}
          zIndex={1}
          text="NEW"
        />
      )}
      <Card
        flexWrap={{ xs: 'no-wrap' }}
        flexDirection="column"
        contentWidth={1}
        backgroundColor="transparent"
        contentHeight="100%"
        isDisabled={!item.isAvailable}
        image={
          item.image
            ? {
                url: item.image,
                alt: item.imageAlt,
                aspectRatio: '1:1',
              }
            : null
        }
        justifyContent="space-between"
        padding={null}
        borderRadius={8}
        rounded
        isEligibleForDeliveryDiscount={isEligibleForDeliveryDiscount}
        isDiscountApplied={isDiscountApplied}
        deliveryDiscountSpacing={{ xs: '2.5rem', lg: 2, xxl: '1.5rem' }}
        isItemInCart={isItemInCart}
      >
        <StyledCartIndicator
          top="12px"
          right="12px"
          position="absolute"
          visibility={isItemInCart ? 'unset' : 'hidden'}
          bg="black"
          height={{ xs: 22, md: 30 }}
          width={{ xs: 22, md: 30 }}
          minHeight={{ xs: 22, md: 30 }}
          minWidth={{ xs: 22, md: 30 }}
          quantityInCart={quantityInCart}
        />
        <Flex height="100%">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
            py={{ xs: '0.75rem', xxl: '0.75rem' }}
          >
            <Box display="flex" flexDirection="column" width="100%">
              <P variant={2} fontWeight="bold" mb="0.5rem" color={!item.isAvailable ? 'grey600' : undefined}>
                <TextClamp>{item.groupName || item.name}</TextClamp>
              </P>
              <Box mt={{ md: 'auto' }}>
                {item.hasVariablePrice && (
                  <P variant={4} mb={{ xs: '.125rem', md: '.25rem' }} mt={0.5} color="greyPrimary">
                    From
                  </P>
                )}
                <Flex flexDirection={{ xs: 'column', md: 'row' }} mb={0.5} justifyContent="space-between">
                  <Flex>
                    {itemPrice && (
                      <P variant={3} mr=".5rem" color={!item.isAvailable ? 'greyPrimary' : undefined}>
                        {itemPrice}
                      </P>
                    )}
                    {itemKilojoules && (
                      <P color="greyPrimary" variant={3}>
                        {itemKilojoules}
                      </P>
                    )}
                  </Flex>

                  {item.formattedDietaryPreferencesList && (
                    <Box>
                      <Tags labels={item.formattedDietaryPreferencesList} size="small" />
                    </Box>
                  )}
                </Flex>
              </Box>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              width={{ xs: 'fit-content', lg: '100%' }}
              mt={{ xs: '16px', lg: '20px' }}
            >
              {isCateringMenu && (cateringMinQty > 1 || cateringQtyIncrement > 1) && (
                <P variant={4} mb="12px" color="grey500">
                  <TextClamp>
                    {cateringMinQty && `Minimum qty ${cateringMinQty}`}
                    {cateringMinQty && cateringQtyIncrement && '. '}
                    {cateringQtyIncrement && `Order in multiples of ${cateringQtyIncrement}.`}
                  </TextClamp>
                </P>
              )}

              <Box display="flex" flexDirection="column" gap="8px" width="100%">
                <QuantityPicker
                  width="100%"
                  max={isCateringMenu ? UPSELL_ITEM_MAX_QUANTITY_CATERING : UPSELL_ITEM_MAX_QUANTITY}
                  quantity={quantitySelected}
                  increment={() => {
                    if (isCateringMenu) {
                      // Use catering increment logic
                      const nextQuantity = getNextCateringQuantity(
                        quantitySelected,
                        cateringMinQty,
                        cateringQtyIncrement,
                        UPSELL_ITEM_MAX_QUANTITY_CATERING
                      );
                      const increment = nextQuantity - quantitySelected;
                      if (increment > 0) {
                        addItem(increment);
                      }
                    } else {
                      addItem(1);
                    }
                  }}
                  decrement={() => {
                    if (isCateringMenu) {
                      // Use catering decrement logic
                      const prevQuantity = getPreviousCateringQuantity(
                        quantitySelected,
                        cateringMinQty,
                        cateringQtyIncrement
                      );
                      const decrement = quantitySelected - prevQuantity;
                      if (decrement > 0) {
                        removeItem(decrement);
                      }
                    } else {
                      removeItem(1);
                    }
                  }}
                  onQuantityChange={newQuantity => {
                    if (isCateringMenu) {
                      // For catering, we need to respect min/increment rules
                      const difference = newQuantity - quantitySelected;
                      if (difference > 0) {
                        addItem(difference);
                      } else if (difference < 0) {
                        removeItem(Math.abs(difference));
                      }
                    } else if (newQuantity > quantitySelected) {
                      addItem(newQuantity - quantitySelected);
                    } else if (newQuantity < quantitySelected) {
                      removeItem(quantitySelected - newQuantity);
                    }
                  }}
                  isCateringMenu={isCateringMenu}
                  minQty={cateringMinQty}
                  qtyIncrement={cateringQtyIncrement}
                  disableInput={isCateringMenu && (cateringMinQty > 1 || cateringQtyIncrement > 1)}
                />
                <Button
                  isFullWidth
                  variant="primary"
                  disabled={
                    !item.isAvailable || (isCateringMenu ? quantitySelected < cateringMinQty : quantitySelected === 0)
                  }
                  onClick={() => {
                    addSelectedItemsToCart();
                    removeItem(quantitySelected);
                  }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        </Flex>
      </Card>
    </Flex>
  );
};

UpsellItemCard.propTypes = {
  item: productPropTypes.isRequired,
  isNew: PropTypes.bool,
  addItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  addSelectedItemsToCart: PropTypes.func.isRequired,
  isItemInCart: PropTypes.bool,
  isDiscountApplied: PropTypes.bool,
  quantitySelected: PropTypes.number,
  isConfigurableFlowEnabled: PropTypes.bool,
  requiresConfiguration: PropTypes.bool,
  onAdd: PropTypes.func,
  onCustomise: PropTypes.func,
  onIncrement: PropTypes.func,
  onDecrement: PropTypes.func,
};

UpsellItemCard.defaultProps = {
  isNew: false,
  isItemInCart: false,
  isDiscountApplied: false,
  quantitySelected: 0,
  isConfigurableFlowEnabled: false,
  requiresConfiguration: null,
  onAdd: noop,
  onCustomise: noop,
  onIncrement: noop,
  onDecrement: noop,
};

const ComposedUpsellItemCard = observer(UpsellItemCard);
// ConfigurableUpsellItemTile is exported for unit testing its in-cart rendering, which the
// wrapper only reaches via CartStore.
export { ComposedUpsellItemCard as UpsellItemCard, ConfigurableUpsellItemTile };
